
import React from 'react'
import { mdx } from '@mdx-js/react'

/* @jsxRuntime classic */
/* @jsx mdx */

import DocsBeta from 'src/components/docs/DocsBeta/index.tsx';
import styles from '../core-exchange.module.scss';
export const metadata = {
  toc: true,
  subnav: true,
  title: 'Testing & validation',
  layout: 'guide',
  description: 'Test and validate your Core Exchange integration using the Validator',
  parentTocLevel: 2,
  childTocLevel: 3,
  secondLevelToc: true,
  alwaysExpand: true
};
const makeShortcode = name => function MDXDefaultShortcode(props) {
      console.warn("Component " + name + " was not imported, exported, or provided by MDXProvider as global scope")
      return <div {...props}/>
    };
const Header = makeShortcode("Header");
const Image = makeShortcode("Image");
const layoutProps = {
  metadata
};
const MDXLayout = "wrapper"
export default function MDXContent({
  components,
  ...props
}) {
  return <MDXLayout {...layoutProps} {...props} components={components} mdxType="MDXLayout">


    <div className={styles.page}>
      <Header title='Testing & validation' subtitle={metadata.description} mdxType="Header" />
      <h2 {...{
        "id": "overview"
      }}>{`Overview`}</h2>
      <p>{`All testing is completed in the `}<a parentName="p" {...{
          "href": "/core-exchange/docs/dashboard-overview"
        }}>{`Data Partner Dashboard`}</a>{` using the Validator. To access the Validator, open the Dashboard and navigate to the `}<strong parentName="p">{`Validator`}</strong>{` section. Validation is required before production access can be enabled and is expected to be iterative.`}</p>
      <p>{`Testing includes:`}</p>
      <ul>
        <li parentName="ul">{`Authentication testing (OAuth)`}</li>
        <li parentName="ul">{`Data aggregation testing (FDX endpoints)`}</li>
      </ul>
      <p>{`Validation failures are normal early in the process. Most partners iterate several times before achieving full passing status.`}</p>
      <h2 {...{
        "id": "authentication-testing-oauth"
      }}>{`Authentication testing (OAuth)`}</h2>
      <p>{`Authentication testing validates that Plaid can:`}</p>
      <ul>
        <li parentName="ul">{`Redirect users to your authorization flow`}</li>
        <li parentName="ul">{`Complete OAuth authentication, including MFA if applicable`}</li>
        <li parentName="ul">{`Exchange authorization codes for tokens`}</li>
        <li parentName="ul">{`Refresh access tokens successfully`}</li>
      </ul>
      <p>{`Because users are redirected into your OAuth flow, early authentication errors may occur outside of Plaid's visibility. Detailed error information typically becomes available starting at the token exchange step. If issues arise, Plaid can work with you to investigate behavior and logs on both sides of the integration.`}</p>
      <p>{`For a complete end-to-end OAuth flow with Core Exchange APIs, see `}<a parentName="p" {...{
          "href": "/core-exchange/docs/authentication#try-the-interactive-demo"
        }}>{`FDX Explorer`}</a>{`. Use the Validator tool in the Dashboard to validate your own integration.`}</p>
      <p>{`Authentication can be validated by:`}</p>
      <ul>
        <li parentName="ul">{`Completing the OAuth flow with test credentials`}</li>
        <li parentName="ul">{`Supplying a valid access token directly, if supported`}</li>
      </ul>
      <p>{`The Dashboard is optimized for OIDC-based configurations, but OIDC is not required. Plaid can manually configure OAuth-only integrations if needed.`}</p>
      <h2 {...{
        "id": "data-endpoint-validation-fdx"
      }}>{`Data endpoint validation (FDX)`}</h2>
      <p>{`Data validation confirms that Plaid can reliably retrieve user data after authentication.`}</p>
      <p>{`The Validator supports:`}</p>
      <ul>
        <li parentName="ul"><strong parentName="li">{`End-to-end:`}</strong>{` simulate a full user journey connecting with an app by testing all endpoints`}</li>
        <li parentName="ul"><strong parentName="li">{`Unit tests:`}</strong>{` validate a single endpoint or flow in isolation and can be run against a specific account`}</li>
      </ul>
      <Image src="/assets/img/core-exchange/validator-unit-test.png" alt="Unit test configuration panel showing environment selector set to Development, connection options for OAuth or Access Token authentication, and a checkbox to test against a single account ID with an input field for the account ID" caption="Configure unit tests to validate specific endpoints and accounts" mdxType="Image" />
      <p>{`Tests fall into two categories:`}</p>
      <ul>
        <li parentName="ul"><strong parentName="li">{`User-initiated:`}</strong>{` tests run manually in the Validator while user is present`}</li>
        <li parentName="ul"><strong parentName="li">{`Batch:`}</strong>{` tests run automatically in the background on a set schedule (~every 6 hours) to verify refresh flows by requesting new access tokens and updating user data`}</li>
      </ul>
      <p>{`Before production access:`}</p>
      <ul>
        <li parentName="ul">{`All required endpoints must pass validation`}</li>
        <li parentName="ul">{`Required endpoints depend on supported account types`}</li>
      </ul>
      <h3 {...{
        "id": "end-to-end-validation"
      }}>{`End-to-end validation`}</h3>
      <p>{`End-to-end validation runs a complete validation flow across all endpoints and accounts. Use end-to-end validation when:`}</p>
      <ul>
        <li parentName="ul">{`You are ready for the full validation required before Production.`}</li>
        <li parentName="ul">{`You need to validate how multiple endpoints work together across a complete workflow.`}</li>
      </ul>
      <h3 {...{
        "id": "supported-endpoints"
      }}>{`Supported endpoints`}</h3>
      <p>{`The Validator supports the following FDX v6 endpoints:`}</p>
      <ul>
        <li parentName="ul"><inlineCode parentName="li">{`/customers/current`}</inlineCode>{` (only if OIDC is not used)`}</li>
        <li parentName="ul"><inlineCode parentName="li">{`/accounts`}</inlineCode></li>
        <li parentName="ul"><inlineCode parentName="li">{`/accounts/{accountId}`}</inlineCode></li>
        <li parentName="ul"><inlineCode parentName="li">{`/accounts/{accountId}/payment-networks`}</inlineCode></li>
        <li parentName="ul"><inlineCode parentName="li">{`/accounts/{accountId}/contact`}</inlineCode></li>
        <li parentName="ul"><inlineCode parentName="li">{`/accounts/{accountId}/transactions`}</inlineCode></li>
        <li parentName="ul"><inlineCode parentName="li">{`/accounts/{accountId}/statements`}</inlineCode></li>
        <li parentName="ul"><inlineCode parentName="li">{`/accounts/{accountId}/statements/{statementId}`}</inlineCode></li>
        <li parentName="ul"><inlineCode parentName="li">{`/accounts/{accountId}/asset-transfer-networks`}</inlineCode></li>
      </ul>
      <h3 {...{
        "id": "understanding-results"
      }}>{`Understanding results`}</h3>
      <p>{`Each validation rule displays:`}</p>
      <ul>
        <li parentName="ul"><strong parentName="li">{`Pass/Fail status`}</strong></li>
        <li parentName="ul"><strong parentName="li">{`Validation message`}</strong>{` explaining what was checked and why it failed`}</li>
      </ul>
      <p>{`Example messages:`}</p>
      <ul>
        <li parentName="ul">{`Schema: "Field X is required by FDX v6."`}</li>
        <li parentName="ul">{`Plaid-specific: "`}<inlineCode parentName="li">{`accountMask`}</inlineCode>{` must match last 4 digits of `}<inlineCode parentName="li">{`accountNumber`}</inlineCode>{`."`}</li>
      </ul>
      <p>{`Use these messages to fix your API responses, then re-run the specific endpoint or account in the Validator without rerunning the entire validation.`}</p>
      <Image src="/assets/img/core-exchange/validator-unit-test-endpoints.png" alt="Endpoint list showing individual Run buttons for each endpoint. GET Details shows passed validation with green checkmark and 200 status messages. Other endpoints like Payment networks, Contact info, Transactions, Statements, and Asset transfer networks each have their own Run button." caption="Run individual endpoints independently" mdxType="Image" />
      <h3 {...{
        "id": "what-gets-validated"
      }}>{`What gets validated`}</h3>
      <p><strong parentName="p">{`FDX schema validation`}</strong>{` ensures your JSON matches the FDX contract for that endpoint.`}</p>
      <p><strong parentName="p">{`Plaid-specific validation rules`}</strong>{` verify that data can flow through Plaid's production API successfully. These checks validate the fields that Plaid extracts and uses for each product, ensuring your FDX responses contain the data structure Plaid expects.`}</p>
      <p>{`The Validator performs the same checks as Plaid's production data extraction, including:`}</p>
      <ul>
        <li parentName="ul"><strong parentName="li">{`Access token validity`}</strong>{`: Tokens must be active and not expired`}</li>
        <li parentName="ul"><strong parentName="li">{`Date range formatting`}</strong>{`: Start and end dates must be in `}<inlineCode parentName="li">{`YYYY-MM-DD`}</inlineCode>{` format with start date before end date`}</li>
        <li parentName="ul"><strong parentName="li">{`Required field presence`}</strong>{`: Each product validates its core fields are present and properly formatted`}</li>
        <li parentName="ul"><strong parentName="li">{`Data structure`}</strong>{`: Response objects match expected Plaid API schemas`}</li>
      </ul>
      <p>{`For example, when validating `}<inlineCode parentName="p">{`/accounts/{accountId}/transactions`}</inlineCode>{`, the Validator checks:`}</p>
      <ul>
        <li parentName="ul">{`Transaction `}<inlineCode parentName="li">{`name`}</inlineCode>{`, `}<inlineCode parentName="li">{`amount`}</inlineCode>{`, and `}<inlineCode parentName="li">{`date`}</inlineCode>{` fields are present`}</li>
        <li parentName="ul">{`Amounts are numeric and properly formatted`}</li>
        <li parentName="ul">{`Pagination works correctly`}</li>
      </ul>
      <p><strong parentName="p">{`Validation scope:`}</strong>{` Only 200 responses are rule-validated. Non-200 responses show the failed call details instead.`}</p>
      <h3 {...{
        "id": "multi-institution-testing"
      }}>{`Multi-institution testing`}</h3>
      <p>{`For platforms managing multiple institutions, select the specific institution you want to test from the institution dropdown. Tests then behave the same as for a single Data Partner.`}</p>
      <h2 {...{
        "id": "related-documentation"
      }}>{`Related documentation`}</h2>
      <ul>
        <li parentName="ul"><a parentName="li" {...{
            "href": "/core-exchange/docs/implementation-checklist#architecture-diagram"
          }}>{`Architecture diagram`}</a></li>
        <li parentName="ul"><a parentName="li" {...{
            "href": "/core-exchange/docs/reference"
          }}>{`Core Exchange API reference`}</a></li>
        <li parentName="ul"><a parentName="li" {...{
            "href": "/core-exchange/docs/authentication"
          }}>{`Authentication overview`}</a></li>
        <li parentName="ul"><a parentName="li" {...{
            "href": "/core-exchange/docs/authentication#try-the-interactive-demo"
          }}>{`FDX Explorer`}</a></li>
        <li parentName="ul"><a parentName="li" {...{
            "href": "/core-exchange/docs/mock-server"
          }}>{`Mock server for early testing`}</a>{`. Plaid hosts a mock FDX server at `}<inlineCode parentName="li">{`api.firstplaidypusbank.plaid.com`}</inlineCode>{` that returns sample FDX v6 data. Use it to explore FDX data payloads before building your own endpoints.`}</li>
      </ul>
    </div>
    </MDXLayout>;
}
;
MDXContent.isMDXComponent = true;

            export const tableOfContents = [
      {
        id: "overview",
        level: 2,
        title: "Overview",
      },
{
        id: "authentication-testing-oauth",
        level: 2,
        title: "Authentication testing (OAuth)",
      },
{
        id: "data-endpoint-validation-fdx",
        level: 2,
        title: "Data endpoint validation (FDX)",
      },
{
        id: "end-to-end-validation",
        level: 3,
        title: "End-to-end validation",
      },
{
        id: "supported-endpoints",
        level: 3,
        title: "Supported endpoints",
      },
{
        id: "understanding-results",
        level: 3,
        title: "Understanding results",
      },
{
        id: "what-gets-validated",
        level: 3,
        title: "What gets validated",
      },
{
        id: "multi-institution-testing",
        level: 3,
        title: "Multi-institution testing",
      },
{
        id: "related-documentation",
        level: 2,
        title: "Related documentation",
      }
];
          
layoutProps.tableOfContents = tableOfContents;
MDXContent.layoutProps = layoutProps;

MDXContent.layout = function (props) { return <DocsBeta {...props} /> };
